// /** @jsx jsx */
import React, { useState, useCallback, useEffect } from 'react'
import { jsx, Box } from 'theme-ui'
import { Button, Input, Table } from 'antd'
import {
  isValidKeyValidationHost,
  KeyValidation,
  SUPPORTED_KEY_VALIDATION_DETECTORS,
} from '@spectral/types'
import {
  ContentPanel,
  ContentPanelHeader,
} from '../../../components/panels/content'

interface KeysValidationInputProps {
  disabled: boolean
  currentKeyValidations: Array<KeyValidation>
  selectedAssetType: string
  onSubmit: (keyValidations: Array<KeyValidation>) => Promise<void>
}

// Supported detectors configuration using shared constant
const SUPPORTED_DETECTORS = SUPPORTED_KEY_VALIDATION_DETECTORS.map((id) => {
  switch (id) {
    case 'ARTIF004':
      return {
        id,
        name: 'Artifactory Access Token',
        description: 'Configure custom host validation for Artifactory tokens',
        placeholder: 'https://my.artifactory.example.com',
      }
    default:
      return {
        id,
        name: id,
        description: `Configure custom host validation for ${id}`,
        placeholder: 'https://your.host.example.com',
      }
  }
})

type SupportedDetectorId = typeof SUPPORTED_KEY_VALIDATION_DETECTORS[number]

const KeysValidationInput: React.FC<KeysValidationInputProps> = ({
  disabled,
  currentKeyValidations,
  selectedAssetType: _selectedAssetType,
  onSubmit,
}) => {
  // Create a map of detector configs from current validations and supported detectors
  const [detectorConfigs, setDetectorConfigs] = useState<
    Record<SupportedDetectorId, { host: string; isConfigured: boolean }>
  >(() => {
    const configs = {} as Record<
      SupportedDetectorId,
      { host: string; isConfigured: boolean }
    >

    SUPPORTED_DETECTORS.forEach((detector) => {
      const existingValidation = currentKeyValidations.find(
        (validation) => validation.detectorId === detector.id
      )
      configs[detector.id] = {
        host: existingValidation?.host || '',
        isConfigured: !!existingValidation,
      }
    })

    return configs
  })

  // Track editing state for each detector
  const [editingStates, setEditingStates] = useState<
    Record<SupportedDetectorId, { isEditing: boolean; tempHost: string }>
  >(() => {
    const states = {} as Record<
      SupportedDetectorId,
      { isEditing: boolean; tempHost: string }
    >

    SUPPORTED_DETECTORS.forEach((detector) => {
      states[detector.id] = {
        isEditing: !detectorConfigs[detector.id]?.isConfigured,
        tempHost: detectorConfigs[detector.id]?.host || '',
      }
    })

    return states
  })

  // Sync local state with props when currentKeyValidations changes
  useEffect(() => {
    const newConfigs = {} as Record<
      SupportedDetectorId,
      { host: string; isConfigured: boolean }
    >
    const newEditingStates = {} as Record<
      SupportedDetectorId,
      { isEditing: boolean; tempHost: string }
    >

    SUPPORTED_DETECTORS.forEach((detector) => {
      const existingValidation = currentKeyValidations.find(
        (validation) => validation.detectorId === detector.id
      )
      const isConfigured = !!existingValidation
      const host = existingValidation?.host || ''

      newConfigs[detector.id] = {
        host,
        isConfigured,
      }

      newEditingStates[detector.id] = {
        isEditing: !isConfigured,
        tempHost: host,
      }
    })

    setDetectorConfigs(newConfigs)
    setEditingStates(newEditingStates)
  }, [currentKeyValidations])

  const isValidUrl = useCallback((url) => isValidKeyValidationHost(url), [])

  const handleTempHostChange = useCallback(
    (detectorId: SupportedDetectorId, newHost: string) => {
      setEditingStates((prev) => ({
        ...prev,
        [detectorId]: {
          ...prev[detectorId],
          tempHost: newHost,
        },
      }))
    },
    []
  )

  const handleStartEdit = useCallback(
    (detectorId: SupportedDetectorId) => {
      setEditingStates((prev) => ({
        ...prev,
        [detectorId]: {
          ...prev[detectorId],
          isEditing: true,
          tempHost: detectorConfigs[detectorId]?.host || '',
        },
      }))
    },
    [detectorConfigs]
  )

  const handleSaveHost = useCallback(
    (detectorId: SupportedDetectorId) => {
      const editingState = editingStates[detectorId]
      const trimmedHost = editingState.tempHost.trim()

      // Validate URL format if not empty
      if (trimmedHost && !isValidUrl(trimmedHost)) {
        return // Don't save invalid URLs
      }

      // Update local config
      setDetectorConfigs((prev) => ({
        ...prev,
        [detectorId]: {
          host: trimmedHost,
          isConfigured: !!trimmedHost,
        },
      }))

      // Exit editing mode
      setEditingStates((prev) => ({
        ...prev,
        [detectorId]: {
          ...prev[detectorId],
          isEditing: !trimmedHost, // Stay in editing mode if no host provided
        },
      }))

      // Prepare updated validations for server
      const updatedValidations: KeyValidation[] = []

      SUPPORTED_DETECTORS.forEach((detector) => {
        if (detector.id === detectorId) {
          // Add current detector only if host is valid and not empty
          if (trimmedHost && isValidUrl(trimmedHost)) {
            updatedValidations.push({
              detectorId: detector.id,
              host: trimmedHost,
            })
          }
        } else {
          // Add other detectors if they have valid hosts
          const config = detectorConfigs[detector.id as SupportedDetectorId]
          if (config && config.host.trim() && isValidUrl(config.host.trim())) {
            updatedValidations.push({
              detectorId: detector.id,
              host: config.host.trim(),
            })
          }
        }
      })

      onSubmit(updatedValidations)
    },
    [detectorConfigs, editingStates, onSubmit, isValidUrl]
  )

  const handleCancelEdit = useCallback(
    (detectorId: SupportedDetectorId) => {
      setEditingStates((prev) => ({
        ...prev,
        [detectorId]: {
          ...prev[detectorId],
          isEditing: false,
          tempHost: detectorConfigs[detectorId]?.host || '',
        },
      }))
    },
    [detectorConfigs]
  )

  const handleClearHost = useCallback(
    (detectorId: SupportedDetectorId) => {
      // Update local config to empty
      setDetectorConfigs((prev) => ({
        ...prev,
        [detectorId]: {
          host: '',
          isConfigured: false,
        },
      }))

      // Set editing state
      setEditingStates((prev) => ({
        ...prev,
        [detectorId]: {
          isEditing: true,
          tempHost: '',
        },
      }))

      // Prepare updated validations for server (remove this detector)
      const updatedValidations: KeyValidation[] = []

      SUPPORTED_DETECTORS.forEach((detector) => {
        if (detector.id !== detectorId) {
          // Add other detectors if they have valid hosts
          const config = detectorConfigs[detector.id as SupportedDetectorId]
          if (config && config.host.trim() && isValidUrl(config.host.trim())) {
            updatedValidations.push({
              detectorId: detector.id,
              host: config.host.trim(),
            })
          }
        }
      })

      onSubmit(updatedValidations)
    },
    [detectorConfigs, onSubmit, isValidUrl]
  )

  const columns = [
    {
      title: 'Detector/Rule',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name: string, detector: typeof SUPPORTED_DETECTORS[number]) => (
        <Box>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            {detector.id}
          </Box>
          <Box sx={{ fontSize: '12px', color: '#666' }}>{name}</Box>
        </Box>
      ),
    },
    {
      title: 'Host URL',
      dataIndex: 'id',
      key: 'host',
      width: '50%',
      render: (detectorId: SupportedDetectorId) => {
        const config = detectorConfigs[detectorId]
        const editingState = editingStates[detectorId]
        const detector = SUPPORTED_DETECTORS.find((d) => d.id === detectorId)

        if (editingState.isEditing) {
          return (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Input
                value={editingState.tempHost}
                onChange={(e) =>
                  handleTempHostChange(detectorId, e.target.value)
                }
                placeholder={detector?.placeholder}
                disabled={disabled}
                style={{
                  fontFamily: 'monospace',
                  borderColor:
                    editingState.tempHost.trim() &&
                    !isValidUrl(editingState.tempHost.trim())
                      ? '#ff4d4f'
                      : undefined,
                }}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveHost(detectorId)}
                disabled={
                  disabled ||
                  (editingState.tempHost.trim() &&
                    !isValidUrl(editingState.tempHost.trim()))
                }
              >
                Done
              </Button>
              {config.isConfigured && (
                <Button
                  size="small"
                  onClick={() => handleCancelEdit(detectorId)}
                  disabled={disabled}
                >
                  Cancel
                </Button>
              )}
            </Box>
          )
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                fontFamily: 'monospace',
                fontSize: '14px',
                flex: 1,
                color: config.host ? '#000' : '#999',
              }}
            >
              {config.host || 'Not configured'}
            </Box>
            <Button
              type="link"
              size="small"
              onClick={() => handleStartEdit(detectorId)}
              disabled={disabled}
            >
              Edit
            </Button>
          </Box>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'id',
      key: 'status',
      width: '20%',
      render: (detectorId: SupportedDetectorId) => {
        const config = detectorConfigs[detectorId]
        const hasValidHost =
          config.host.trim() && isValidUrl(config.host.trim())

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: hasValidHost ? '#52c41a' : '#d9d9d9',
              }}
            />
            <Box
              sx={{
                fontSize: '12px',
                color: hasValidHost ? '#52c41a' : '#999',
              }}
            >
              {hasValidHost ? 'Configured' : 'Not configured'}
            </Box>
            {hasValidHost && (
              <Button
                type="link"
                size="small"
                danger
                onClick={() => handleClearHost(detectorId)}
                disabled={disabled}
              >
                Clear
              </Button>
            )}
          </Box>
        )
      },
    },
  ]

  return (
    <Box sx={{ mt: '30px', mb: '30px' }}>
      <ContentPanel>
        <ContentPanelHeader>Keys Validation Configuration</ContentPanelHeader>
        <Box sx={{ p: '16px' }}>
          <Box sx={{ mb: '16px', color: '#666' }}>
            Configure custom host validation for specific detectors. This allows
            you to specify custom endpoints for key validation.
          </Box>

          <Table
            dataSource={SUPPORTED_DETECTORS}
            columns={columns}
            pagination={false}
            rowKey={(record) => record.id}
            size="small"
            bordered
          />

          <Box sx={{ mt: '16px', fontSize: '12px', color: '#666' }}>
            <Box sx={{ mb: '8px', fontWeight: 'bold' }}>Instructions:</Box>
            <Box sx={{ mb: '4px' }}>
              • Enter a valid HTTPS URL (localhost is not allowed) for each
              detector you want to configure
            </Box>
            <Box sx={{ mb: '4px' }}>
              • Detectors with valid host URLs will be automatically configured
            </Box>
            <Box>
              • Clear the host URL to disable validation with custom host for
              that detector
            </Box>
          </Box>
        </Box>
      </ContentPanel>
    </Box>
  )
}

export default KeysValidationInput
